/**
 * User Menu Component
 * 
 * Displays user account icon with dropdown menu containing:
 * - Language selector and theme toggle (always)
 * - Sign in option (unauthenticated users)
 * - Profile and sign out (authenticated users)
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PersonIcon from '@mui/icons-material/Person';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { useAuth } from '../auth';
import { config } from '../config';
import { useThemeModeContext } from './ThemeModeContext';
import { getSelectedLanguage, LANGUAGE_STORAGE_KEY } from './LanguageSelector';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, normalizeUiLanguage } from '../i18n/languages';

const LANGUAGE_CODES = SUPPORTED_LANGUAGES;

export function UserMenu() {
  const { t, i18n } = useTranslation('common');
  const { isAuthenticated, isLoading, user, login, logout } = useAuth();
  const { mode, toggleMode } = useThemeModeContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);
  const [language, setLanguage] = useState<string>(getSelectedLanguage);
  const open = Boolean(anchorEl);
  const langOpen = Boolean(langAnchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setLangAnchorEl(null);
  };

  const handleLangOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangClose = () => {
    setLangAnchorEl(null);
  };

  const handleLogin = () => {
    handleClose();
    login();
  };

  const handleProfile = () => {
    handleClose();
    const profileUrl = config.oidc.profileUrl;
    if (profileUrl) {
      window.open(profileUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleLogout = () => {
    handleClose();
    logout();
  };

  const handleToggleTheme = () => {
    toggleMode();
  };

  const handleLanguageSelect = (code: string) => {
    setLanguage(code);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
    // Not every content language has a translation bundle; map down so the
    // interface stays renderable while results follow the chosen language.
    i18n.changeLanguage(normalizeUiLanguage(code) ?? DEFAULT_LANGUAGE);
    window.dispatchEvent(new CustomEvent('languageChange', { detail: code }));
    handleLangClose();
  };

  const getInitials = () => {
    if (!user) return '';
    return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
        <CircularProgress size={24} color="inherit" />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {/* Greeting text for authenticated users */}
      {isAuthenticated && user && (
        <Typography
          variant="body2"
          sx={{
            mr: 1,
            display: { xs: 'none', sm: 'block' },
            color: 'inherit',
          }}
        >
          {t('user.greeting', { name: user.firstName })}
        </Typography>
      )}

      {/* Account icon/avatar — always opens the menu */}
      <Tooltip title={isAuthenticated ? t('user.account') : t('user.signIn')}>
        <IconButton
          onClick={handleClick}
          size="large"
          edge="end"
          color="inherit"
          aria-label="account menu"
          aria-controls={open ? 'account-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          {isAuthenticated && user ? (
            user.picture ? (
              <Avatar
                src={user.picture}
                alt={`${user.firstName} ${user.lastName}`}
                sx={{ width: 32, height: 32 }}
              />
            ) : (
              <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.dark' }}>
                {getInitials()}
              </Avatar>
            )
          ) : (
            <AccountCircleIcon sx={{ width: 36, height: 36 }} />
          )}
        </IconButton>
      </Tooltip>

      {/* Dropdown menu — shown for all users */}
      <Menu
        id="account-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
              mt: 1,
              minWidth: 200,
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
      >
        {/* User info header (authenticated only) */}
        {isAuthenticated && user && (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={500}>
              {user.firstName} {user.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user.email}
            </Typography>
          </Box>
        )}

        {isAuthenticated && <Divider />}

        {/* Language — opens submenu */}
        <MenuItem onClick={handleLangOpen}>
          <ListItemIcon>
            <LanguageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t(`languages.${language}`)}</ListItemText>
          <ChevronRightIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
        </MenuItem>

        {/* Theme toggle */}
        <MenuItem onClick={handleToggleTheme}>
          <ListItemIcon>
            {mode === 'light' ? <DarkModeOutlinedIcon fontSize="small" /> : <LightModeOutlinedIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>
            {mode === 'light'
              ? t('theme.switchToDark', 'Dark mode')
              : t('theme.switchToLight', 'Light mode')}
          </ListItemText>
        </MenuItem>

        <Divider />

        {/* Authenticated: Profile + Logout */}
        {isAuthenticated && (
          <>
            {config.oidc.profileUrl && (
              <MenuItem onClick={handleProfile}>
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('user.profile')}</ListItemText>
                <OpenInNewIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
              </MenuItem>
            )}
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('user.signOut')}</ListItemText>
            </MenuItem>
          </>
        )}

        {/* Unauthenticated: Sign in */}
        {!isAuthenticated && (
          <MenuItem onClick={handleLogin}>
            <ListItemIcon>
              <LoginIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('user.signIn')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Language submenu */}
      <Menu
        anchorEl={langAnchorEl}
        open={langOpen}
        onClose={handleLangClose}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
              minWidth: 160,
            },
          },
        }}
      >
        {LANGUAGE_CODES.map((code) => (
          <MenuItem key={code} onClick={() => handleLanguageSelect(code)} selected={code === language}>
            <ListItemIcon>
              {code === language ? <CheckIcon fontSize="small" /> : <Box sx={{ width: 20 }} />}
            </ListItemIcon>
            <ListItemText>{t(`languages.${code}`)}</ListItemText>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              {code.toUpperCase()}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
